"use client";

/* eslint-disable import/order */
/* eslint-disable react-hooks/exhaustive-deps */

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useFetchQuoteData from "@/hooks/api/quote/use-fetch-quote";
import useGtmConfirmationOption from "@/hooks/gtm/confirmation/use-gtm-confirmation-option";
import useUpdateProgressBar from "@/hooks/progress/use-update-progress-bar";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { setQuote } from "@/store/slices/quote/quote.slice";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import QuoteCoverTypeMap from "@/utils/gtm/cover-type-map";
import QuoteDataMap from "@/utils/gtm/quote-data-map";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import { YouAreNowCovered } from "@/config/app-constant";
import useContentSquareConfirmationOption from "@/hooks/contentsquare/use-contentsquare-confirmation-option";

type ConfirmationPageDataProps = {
  progressBarPercent: number;
};

export function useConfirmationPageData({
  progressBarPercent,
}: ConfirmationPageDataProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { hideLoading, showLoading } = useLoading();
  const { data, loading, error } = useFetchQuoteData("", "", "policy");
  const { attributes, referenceId } = useSelector(
    (state: RootState) => state.quote
  );
  const [initiateGtmUpdate, setInitiateGtmUpdate] = useState(false);
  const { pageReferrer, pageUrl, pageTitle } =
    useCustomPageTracking(YouAreNowCovered);
  useUpdateProgressBar(progressBarPercent);

  // GTM Trigger
  let gtmConfirmationData = null;
  let policyPackagesConfirmation = null;
  if (initiateGtmUpdate) {
    gtmConfirmationData = QuoteDataMap(
      attributes,
      "purchase",
      "order confirmation",
      referenceId
    );
    gtmConfirmationData = {
      ...gtmConfirmationData,
      ss_order_metrics: attributes.paymentDetails?.encryptedMarginValue,
      policy_id: attributes.policyNumber ?? "",
      medical_condition_added: attributes.travellerDetails?.traveller?.some(
        (traveller) => traveller.medical
      )
        ? "true"
        : "false",
    };

    if (gtmConfirmationData.ecommerce) {
      gtmConfirmationData.ecommerce = {
        ...gtmConfirmationData.ecommerce,
        payment_type: attributes.paymentDetails?.paymentOption,
        transaction_id: attributes.policyNumber ?? " ",
      };
    }

    if (gtmConfirmationData.ecommerce) {
      gtmConfirmationData.ecommerce = {
        ...gtmConfirmationData.ecommerce,
        coupon: attributes.paymentDetails?.discounts.promoCode?.code,
      };
    }
    policyPackagesConfirmation = QuoteCoverTypeMap(attributes);
  }

  useGtmConfirmationOption({
    confirmationData: gtmConfirmationData,
    policyItems: policyPackagesConfirmation,
    isReady: initiateGtmUpdate,
  });

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  // Contentsquare transaction
  useContentSquareConfirmationOption({
    id: attributes?.policyNumber ?? "",
    revenue: attributes?.paymentDetails?.paymentOptions[0].cardPayment.card
      .price
      ? parseFloat(
          attributes?.paymentDetails?.paymentOptions[0].cardPayment.card.price
        )
      : null,
    currency: "GBP",
    isReady: initiateGtmUpdate,
  });

  useEffect(() => {
    const handleEffect = async () => {
      if (loading) showLoading();
      if (!loading) hideLoading();
      if (!loading && data) {
        dispatch(setQuote(data));
        setInitiateGtmUpdate(true);
      } else if (!loading && error !== null) {
        if (error.response?.status === 401) {
          router.push("/");
        } else {
          dispatch(setApiError(await apiErrorMappings(error)));
        }
      }
    };
    handleEffect();
  }, [data, loading, error, dispatch, router]);
}
